/**
 * GeniusCRM — siyah-beyaz görsel dil (logo ile uyumlu).
 * Durum / uyarı renkleri (success, warning, error) bilinçli olarak renkli kalır.
 */

export const FONT_STACK =
  '"Plus Jakarta Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/** Ana UI vurgusu: nötr (karanlıkta açık ton, aydınlıkta koyu) */
const ui = {
  darkAccent: "#fafafa",
  darkAccentMuted: "#e5e5e5",
  lightAccent: "#0a0a0a",
  lightAccentMuted: "#262626",
};

/** Bildirim ve durum — yüksek kontrast */
export const semantic = {
  success: "#22c55e",
  warning: "#eab308",
  error: "#ef4444",
  successMuted: "rgba(34, 197, 94, 0.15)",
  warningMuted: "rgba(234, 179, 8, 0.18)",
  errorMuted: "rgba(239, 68, 68, 0.15)",
};

export const darkTheme = {
  colors: {
    primary: ui.darkAccent,
    secondary: "#a3a3a3",
    accent: ui.darkAccentMuted,
    background: {
      primary: "#050505",
      secondary: "#0a0a0a",
      tertiary: "#141414",
      glass: "rgba(255, 255, 255, 0.03)",
      card: "rgba(255, 255, 255, 0.03)",
    },
    surface: {
      primary: "rgba(255, 255, 255, 0.04)",
      secondary: "rgba(255, 255, 255, 0.08)",
      glass: "rgba(255, 255, 255, 0.02)",
      card: "rgba(255, 255, 255, 0.05)",
      input: "rgba(255, 255, 255, 0.06)",
      select: "#141414",
      dropdown: "#171717",
      productCard: "rgba(255, 255, 255, 0.04)",
      divider: "rgba(255, 255, 255, 0.06)",
      dashedButton: "rgba(255, 255, 255, 0.05)",
      cancelButton: "rgba(255, 255, 255, 0.08)",
    },
    text: {
      primary: "#fafafa",
      secondary: "#a3a3a3",
      muted: "#737373",
      danger: semantic.error,
      button: "#0a0a0a",
    },
    border: {
      primary: "rgba(255, 255, 255, 0.1)",
      secondary: "rgba(255, 255, 255, 0.22)",
      glass: "rgba(255, 255, 255, 0.06)",
      input: "rgba(255, 255, 255, 0.12)",
      select: "#262626",
      dropdown: "#262626",
      productCard: "rgba(255, 255, 255, 0.1)",
      divider: "rgba(255, 255, 255, 0.08)",
      dashedButton: "rgba(255, 255, 255, 0.14)",
      cancelButton: "rgba(255, 255, 255, 0.12)",
    },
    success: semantic.success,
    warning: semantic.warning,
    error: semantic.error,
    gradient: {
      primary: `linear-gradient(135deg, ${ui.darkAccent} 0%, ${ui.darkAccentMuted} 100%)`,
      secondary: `linear-gradient(135deg, ${ui.darkAccentMuted} 0%, #d4d4d4 100%)`,
      background: "linear-gradient(180deg, #050505 0%, #0a0a0a 45%, #0f0f0f 100%)",
    },
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },
  borderRadius: {
    sm: "4px",
    md: "6px",
    lg: "8px",
    xl: "10px",
    xxl: "12px",
  },
  shadows: {
    sm: "0 1px 0 rgba(0, 0, 0, 0.5)",
    md: "0 4px 14px rgba(0, 0, 0, 0.45)",
    lg: "0 12px 36px rgba(0, 0, 0, 0.5)",
    glass: "0 2px 10px rgba(0, 0, 0, 0.35)",
    dropdown: "0 4px 18px rgba(0, 0, 0, 0.5)",
    productCard: "0 1px 4px rgba(0, 0, 0, 0.4)",
    primaryButton: "0 2px 8px rgba(250, 250, 250, 0.12)",
  },
  effects: {
    backdrop: "blur(8px)",
    glassmorphism: "backdrop-filter: blur(8px)",
    glow: "0 0 0 1px rgba(255, 255, 255, 0.08)",
    blur: "blur(4px)",
  },
  typography: {
    fontFamily: FONT_STACK,
    h1: {
      fontSize: "1.375rem",
      fontWeight: 600,
      letterSpacing: "-0.03em",
      color: "#fafafa",
    },
    h2: {
      fontSize: "1.125rem",
      fontWeight: 600,
      letterSpacing: "-0.02em",
      color: "#f5f5f5",
    },
    h3: {
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "-0.015em",
      color: ui.darkAccent,
    },
    body: {
      fontSize: "0.8125rem",
      lineHeight: 1.55,
      fontWeight: 400,
      letterSpacing: "0.01em",
      color: "#a3a3a3",
    },
  },
};

export const lightTheme = {
  colors: {
    primary: ui.lightAccent,
    secondary: ui.lightAccentMuted,
    accent: "#404040",
    background: {
      primary: "#fafafa",
      secondary: "#f5f5f5",
      tertiary: "#e5e5e5",
      glass: "rgba(255, 255, 255, 0.9)",
      card: "#ffffff",
    },
    surface: {
      primary: "#ffffff",
      secondary: "#f5f5f5",
      glass: "rgba(255, 255, 255, 0.95)",
      card: "#ffffff",
      input: "#ffffff",
      select: "#fafafa",
      dropdown: "#ffffff",
      productCard: "#ffffff",
      divider: "rgba(10, 10, 10, 0.06)",
      dashedButton: "rgba(10, 10, 10, 0.04)",
      cancelButton: "rgba(10, 10, 10, 0.06)",
    },
    text: {
      primary: "#0a0a0a",
      secondary: "#525252",
      muted: "#737373",
      danger: "#dc2626",
      button: "#fafafa",
    },
    border: {
      primary: "rgba(10, 10, 10, 0.1)",
      secondary: "rgba(10, 10, 10, 0.18)",
      glass: "rgba(10, 10, 10, 0.06)",
      input: "rgba(10, 10, 10, 0.14)",
      select: "#e5e5e5",
      dropdown: "#e5e5e5",
      productCard: "rgba(10, 10, 10, 0.1)",
      divider: "rgba(10, 10, 10, 0.08)",
      dashedButton: "rgba(10, 10, 10, 0.15)",
      cancelButton: "rgba(10, 10, 10, 0.1)",
    },
    success: "#16a34a",
    warning: "#ca8a04",
    error: "#dc2626",
    gradient: {
      primary: `linear-gradient(135deg, ${ui.lightAccent} 0%, ${ui.lightAccentMuted} 100%)`,
      secondary: `linear-gradient(135deg, ${ui.lightAccentMuted} 0%, #404040 100%)`,
      background: "linear-gradient(180deg, #fafafa 0%, #f5f5f5 50%, #fafafa 100%)",
    },
  },
  spacing: darkTheme.spacing,
  borderRadius: darkTheme.borderRadius,
  shadows: {
    sm: "0 1px 0 rgba(10, 10, 10, 0.06)",
    md: "0 4px 14px rgba(10, 10, 10, 0.08)",
    lg: "0 12px 36px rgba(10, 10, 10, 0.1)",
    glass: "0 2px 10px rgba(10, 10, 10, 0.06)",
    dropdown: "0 4px 18px rgba(10, 10, 10, 0.12)",
    productCard: "0 1px 4px rgba(10, 10, 10, 0.06)",
    primaryButton: "0 2px 8px rgba(10, 10, 10, 0.15)",
  },
  effects: darkTheme.effects,
  typography: {
    fontFamily: FONT_STACK,
    h1: {
      fontSize: "1.375rem",
      fontWeight: 600,
      letterSpacing: "-0.03em",
      color: "#0a0a0a",
    },
    h2: {
      fontSize: "1.125rem",
      fontWeight: 600,
      letterSpacing: "-0.02em",
      color: "#171717",
    },
    h3: {
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "-0.015em",
      color: ui.lightAccent,
    },
    body: {
      fontSize: "0.8125rem",
      lineHeight: 1.55,
      fontWeight: 400,
      letterSpacing: "0.01em",
      color: "#525252",
    },
  },
};

export const modernTheme = darkTheme;

export const getTheme = (mode) => (mode === "light" ? lightTheme : darkTheme);

/** Primary yüzey (açık ink / koyu ink) üzerinde okunaklı aksiyon butonu. */
export function primaryActionSx(theme, extra = {}) {
  return {
    background: theme?.colors?.gradient?.primary || theme?.colors?.primary,
    color: theme?.colors?.text?.button || "#0a0a0a",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: theme?.shadows?.primaryButton || theme?.shadows?.sm,
    "&:hover": {
      filter: "brightness(0.96)",
      background: theme?.colors?.gradient?.primary || theme?.colors?.primary,
    },
    "& .MuiButton-startIcon, & .MuiButton-endIcon, & svg": {
      color: "inherit",
    },
    ...extra,
  };
}
